import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import TopicTag from 'components/UI/TopicTag';
import { useBusinessUnits } from 'contexts/BusinessUnitsContext';
import WhatsAppWindowBadge from 'pages/Client/components/WhatsAppWindowBadge';
import type React from 'react';
import { type FC, useEffect, useRef, useState } from 'react';
import type { Client } from 'services/types/client';
import { NAVY, RADIUS_ITEM, SURFACE_CONTAINER_LOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';
import formatClientId from 'utils/clientIdFormatter';
import { INSUREX_BUSINESS_UNIT_ID } from 'utils/constants';
import getAdvancePercentage, { getProgressColor, getProgressTextColor } from 'utils/getAdvancePercentage';
import { getWhatsAppWindowExpiry } from 'utils/whatsAppWindow';

const FONT = 'Jost, sans-serif';

const last24hoursDate = new Date();
last24hoursDate.setDate(last24hoursDate.getDate() - 1);

const formatDate = (value: Date | string | null | undefined): string => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  if (d > last24hoursDate) {
    return d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false });
  }
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear().toString().slice(-2)}`;
};

interface ClientListItemProps {
  client: Client;
  isHighlighted: boolean;
  businessUnitType: string;
  isAdmin: boolean;
  onClick: (id: number, event: React.MouseEvent) => void;
}

const TopicChips: FC<{ client: Client }> = ({ client }) => {
  const { getBusinessUnitById } = useBusinessUnits();
  const conversationTaggingConfig = client.businessUnitId
    ? getBusinessUnitById(client.businessUnitId)?.conversationTaggingConfig
    : null;
  const topics = client.topics ?? [];
  const [visibleCount, setVisibleCount] = useState(topics.length);
  const containerRef = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Needed to measure the width
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measure = () => {
      const containerWidth = container.clientWidth;
      if (containerWidth === 0) return;

      const tagChips = Array.from(container.querySelectorAll<HTMLElement>('[data-tag-chip]'));
      const GAP = 4;
      const MORE_CHIP_WIDTH = 32; // approximate width of "+X" chip
      let totalWidth = 0;
      let count = 0;

      for (const chip of tagChips) {
        const chipWidth = chip.offsetWidth + (count > 0 ? GAP : 0);
        const remaining = containerWidth - totalWidth - chipWidth;
        // Reserve space for "+X" chip if there are more tags after this one
        const needsMoreChip = count + 1 < tagChips.length;
        if (needsMoreChip && remaining < MORE_CHIP_WIDTH + GAP) {
          break;
        }
        if (totalWidth + chipWidth > containerWidth) break;
        totalWidth += chipWidth;
        count++;
      }

      setVisibleCount(Math.max(count, 1));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [topics.length]);

  if (!topics.length) return null;

  const hiddenCount = topics.length - visibleCount;

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'center',
        gap: '4px',
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      {topics.map((name, index) => {
        if (index >= visibleCount) return null;
        const cfg = conversationTaggingConfig?.topics?.find((t) => t.name === name);
        const color = cfg?.color || null;
        const inactive = cfg ? !cfg.isActive : false;
        return (
          <TopicTag
            key={name}
            data-tag-chip
            topicName={name}
            color={color}
            inactive={inactive}
            variant="filled"
            sx={{ fontFamily: FONT, borderRadius: RADIUS_ITEM, flexShrink: 0 }}
          />
        );
      })}
      {hiddenCount > 0 && (
        <Chip
          label={`+${hiddenCount}`}
          size="small"
          title={topics.slice(visibleCount).join(', ')}
          sx={{
            fontFamily: FONT,
            fontSize: '0.65rem',
            fontWeight: 600,
            height: 20,
            color: `${NAVY}80`,
            bgcolor: `${NAVY}0A`,
            borderRadius: RADIUS_ITEM,
            flexShrink: 0,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      )}
    </Box>
  );
};

const ROW_SX = { fontFamily: FONT, fontSize: '0.7rem', lineHeight: '1.4' };

const ClientListItem: FC<ClientListItemProps> = ({ client, isHighlighted, businessUnitType, isAdmin, onClick }) => {
  const hasUnread = client.unreadMessagesCount > 0;
  const name =
    client.firstName || client.lastName
      ? `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim()
      : client.phone || client.whatsAppUserId || '—';

  // Mueve Seguro (BU 39): show sales advance progress + client notes, mirroring the leads tab.
  const isMueveSeguro = client.businessUnitId === INSUREX_BUSINESS_UNIT_ID;
  const loanProtectionCount = getAdvancePercentage(client, 'loanProtectionInsurance');
  const unemploymentCount = getAdvancePercentage(client, 'unemploymentInsurance');
  const desgravamenCompletion = loanProtectionCount !== undefined ? `${loanProtectionCount}%` : '-';
  const cesantiaCompletion = unemploymentCount !== undefined ? `${unemploymentCount}%` : '-';

  // Row 2 left: phone + source + campaign
  const metaParts: string[] = [];
  if (client.phone) metaParts.push(client.phone);
  // Independent identifiers: a client can have both, and the BSUID is the one Meta addresses.
  if (client.whatsAppUserId) metaParts.push(`Meta: ${client.whatsAppUserId}`);
  if (client.whatsAppUsername) metaParts.push(`@${client.whatsAppUsername}`);
  if (client.source) metaParts.push(`Origen: ${client.source}`);
  if (client.outboundCampaignId) metaParts.push(`Campaña: ${client.outboundCampaignId}`);
  if (client.user?.name || client.user?.email) {
    metaParts.push(`Vendedor: ${client.user.name || client.user.email}`);
  }

  // Row 3 left: notes, car dealer info, admin notes
  const detailParts: string[] = [];
  if (client.notes) detailParts.push(client.notes);
  if (businessUnitType === 'car_dealership') {
    if (client.brandOfInterest) detailParts.push(client.brandOfInterest);
    if (client.visitDate) {
      detailParts.push(
        new Date(client.visitDate).toLocaleString('es-CL', {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })
      );
    }
  }
  if (isAdmin && client.adminNotes) detailParts.push(client.adminNotes);

  // CRM sale row — only present in the "Pendientes CRM" view (backend populates these per
  // matching sale). Shows the Opportunity reference, insurance type, subetapa and last update.
  const hasCrmSale = !!(client.crmSaleExternalId || client.crmSaleLastUpdate);
  const crmSaleParts: string[] = [];
  if (hasCrmSale) {
    // Prefer Insurex's readable number; fall back to the record Id for sales that predate it.
    const opportunityRef = client.crmSaleOpportunityNumber || client.crmSaleExternalId;
    if (opportunityRef) crmSaleParts.push(`Oportunidad: ${opportunityRef}`);
    if (client.crmSaleInsuranceType) {
      crmSaleParts.push(client.crmSaleInsuranceType === 'unemploymentInsurance' ? 'Cesantía' : 'Desgravamen');
    }
    if (client.crmSaleSubStage) crmSaleParts.push(client.crmSaleSubStage);
    if (client.crmSaleLastUpdate) {
      const updated = new Date(client.crmSaleLastUpdate);
      if (!Number.isNaN(updated.getTime())) {
        crmSaleParts.push(
          `Actualizado: ${updated.toLocaleString('es-CL', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          })}`
        );
      }
    }
  }

  const dateStr = formatDate(client.lastMessageDate);

  const windowExpiresAt = getWhatsAppWindowExpiry(client.clientLastMessageDate);

  return (
    <Box
      onClick={(e) => onClick(client.id, e)}
      sx={{
        display: 'flex',
        gap: 1.5,
        borderRadius: RADIUS_ITEM,
        px: { xs: 1.5, md: 2 },
        py: 0.75,
        cursor: 'pointer',
        borderLeft: isHighlighted ? `3px solid ${NAVY}` : '3px solid transparent',
        bgcolor: isHighlighted ? SURFACE_CONTAINER_LOW : SURFACE_CONTAINER_LOWEST,
        border: '1px solid rgba(21, 24, 58, 0.05)',
        transition: 'background-color 0.15s ease',
        '&:hover': { bgcolor: 'rgba(21, 24, 58, 0.06)' },
        alignItems: 'center',
      }}
    >
      {/* ── Left column ── */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {/* Row 1: Name + ID + tags */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: FONT,
              fontWeight: 600,
              fontSize: '0.85rem',
              color: NAVY,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flexShrink: 1,
              minWidth: 0,
            }}
          >
            {name}
          </Typography>
          <Typography
            sx={{ fontFamily: FONT, fontSize: '0.65rem', color: `${NAVY}77`, whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            #{formatClientId(client.id)}
          </Typography>
          {client.brandOfInterest && (
            <Chip
              label={client.brandOfInterest}
              size="small"
              sx={{
                fontFamily: FONT,
                fontSize: '0.6rem',
                fontWeight: 600,
                height: 18,
                color: NAVY,
                bgcolor: `${NAVY}0A`,
                borderRadius: RADIUS_ITEM,
                flexShrink: 0,
                '& .MuiChip-label': { px: 0.6 },
              }}
            />
          )}
          <TopicChips client={client} />
        </Box>

        {/* Row 2: phone · source · campaign */}
        <Typography
          sx={{
            ...ROW_SX,
            color: `${NAVY}BB`,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {metaParts.join(' · ') || '\u00A0'}
        </Typography>

        {/* Mueve Seguro (BU 39): sales advance progress + notes, like the leads tab */}
        {isMueveSeguro && (
          <>
            <Box sx={{ display: 'flex', gap: 0.5, mt: '2px' }}>
              <Chip
                label={`Desgravamen: ${desgravamenCompletion}`}
                size="small"
                sx={{
                  fontFamily: FONT,
                  fontSize: '0.55rem',
                  height: 18,
                  backgroundColor:
                    loanProtectionCount !== undefined ? getProgressColor(loanProtectionCount) : '#f5f5f5',
                  color: loanProtectionCount !== undefined ? getProgressTextColor(loanProtectionCount) : NAVY,
                  borderRadius: RADIUS_ITEM,
                  '& .MuiChip-label': { px: 0.5 },
                }}
              />
              <Chip
                label={`Cesantia: ${cesantiaCompletion}`}
                size="small"
                sx={{
                  fontFamily: FONT,
                  fontSize: '0.55rem',
                  height: 18,
                  backgroundColor: unemploymentCount !== undefined ? getProgressColor(unemploymentCount) : '#f5f5f5',
                  color: unemploymentCount !== undefined ? getProgressTextColor(unemploymentCount) : NAVY,
                  borderRadius: RADIUS_ITEM,
                  '& .MuiChip-label': { px: 0.5 },
                }}
              />
            </Box>
            {client.notes && (
              <Typography
                sx={{
                  ...ROW_SX,
                  color: `${NAVY}99`,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Notas: {client.notes}
              </Typography>
            )}
          </>
        )}

        {/* Row 3 (Pendientes CRM only): sale Opportunity · type · subetapa · última actualización */}
        {hasCrmSale && crmSaleParts.length > 0 && (
          <Typography
            sx={{
              ...ROW_SX,
              color: NAVY,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textTransform: 'capitalize',
            }}
          >
            {crmSaleParts.join(' · ')}
          </Typography>
        )}
      </Box>

      {/* ── Right column: date + unread badge on top, message preview below ── */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '2px',
          flexShrink: 0,
          minWidth: 160,
          maxWidth: { xs: 210, md: 320 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {client.messagesCount > 0 && (
            <Typography
              sx={{ ...ROW_SX, fontSize: '0.6rem', marginRight: 1, color: `${NAVY}66`, whiteSpace: 'nowrap' }}
            >
              {client.messagesCount} msgs
            </Typography>
          )}
          <WhatsAppWindowBadge windowExpiresAt={windowExpiresAt} />
          <Typography
            sx={{
              ...ROW_SX,
              color: hasUnread ? NAVY : `${NAVY}77`,
              fontWeight: hasUnread ? 600 : 400,
              whiteSpace: 'nowrap',
            }}
          >
            {dateStr || '\u00A0'}
          </Typography>
          {hasUnread && (
            <Box
              sx={{
                bgcolor: NAVY,
                color: '#fff',
                borderRadius: '10px',
                minWidth: 18,
                height: 18,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.6rem',
                fontWeight: 700,
                fontFamily: FONT,
                px: 0.4,
                flexShrink: 0,
              }}
            >
              {client.unreadMessagesCount}
            </Box>
          )}
        </Box>
        {/* Last message preview \u2014 single line, widened for more characters */}
        <Typography
          sx={{
            ...ROW_SX,
            color: `${NAVY}99`,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: { xs: 200, md: 300 },
            textAlign: 'right',
          }}
        >
          {client.lastMessageText || detailParts.join(' \u00B7 ') || '\u00A0'}
        </Typography>
      </Box>
    </Box>
  );
};

export default ClientListItem;
